import { useQuery } from '@tanstack/react-query'
import LabelToggle, {
  LabelToggleOption,
} from 'ext-ui/components/LabelToggle/LabelToggle'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import type { AskReportingChartQuestion } from 'src/agent/reporting/chartQuestion.types'
import ReportingDateRangeControl from 'src/app/Reporting/components/ReportingDateRangeControl'
import {
  createDefaultDateRange,
  formatReportingDateRange,
  PanelGatedState,
  ReportingDateRange,
  reportingPresetLabelKey,
} from 'src/app/Reporting/components/reportingPanelUtils'
import SpendByAccountingCategoryPanel from 'src/app/Reporting/components/SpendByAccountingCategoryPanel'
import SpendByDepartmentPanel from 'src/app/Reporting/components/SpendByDepartmentPanel'
import SpendByPersonPanel from 'src/app/Reporting/components/SpendByPersonPanel'
import SpendByVendorPanel from 'src/app/Reporting/components/SpendByVendorPanel'
import TotalCompanySpendPanel from 'src/app/Reporting/components/TotalCompanySpendPanel'
import * as style from 'src/app/Reporting/Reporting.style'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import { ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { orgCurrencyQueries } from 'src/queries/organizations/currencies/currencies.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'

// Display order: USD first, then CAD.
const currencyValues: ExtendCurrency[] = [
  ExtendCurrency.Usd,
  ExtendCurrency.Cad,
]

export interface ReportingProps {
  onCreateReport?: () => void
  onAskQuestion?: AskReportingChartQuestion
  initialDateRange?: ReportingDateRange
  onDateRangeChange?: (range: ReportingDateRange) => void
  onNavigate?: (destination: string) => void
  resolveGatedDestination?: (
    type: PanelGatedState['type'],
    organizationId: string | undefined,
  ) => string
  embedded?: boolean
}

const Reporting: React.FC<ReportingProps> = ({
  embedded = false,
  onCreateReport,
  onAskQuestion,
  initialDateRange,
  onDateRangeChange,
  onNavigate,
  resolveGatedDestination,
}) => {
  const translate = useTranslate()
  const { data: currentUser } = useQueryOwnUser({})

  const [dateRange, setDateRange] = useState<ReportingDateRange>(
    () => initialDateRange ?? createDefaultDateRange(),
  )
  const [selectedCurrency, setSelectedCurrency] = useState<ExtendCurrency>(
    ExtendCurrency.Usd,
  )

  // Single page-level range: merge each edit into the latest range so a custom
  // calendar pick (two back-to-back partial emits) applies to the live value.
  const handleDateRangeChange = useCallback(
    (range: Partial<ReportingDateRange>) => {
      setDateRange((current) => {
        const next = { ...current, ...range }
        onDateRangeChange?.(next)
        return next
      })
    },
    [onDateRangeChange],
  )

  const handleCurrencyChange = useCallback((option: LabelToggleOption) => {
    setSelectedCurrency(option.value as ExtendCurrency)
  }, [])

  const currencyOptions = useMemo<LabelToggleOption[]>(
    () =>
      currencyValues.map((currency) => ({
        displayName: translate(currency),
        value: currency,
      })),
    [translate],
  )

  const organizationId = currentUser?.organizationId

  const { data: orgCurrencies } = useQuery({
    ...orgCurrencyQueries.list(organizationId),
    enabled: !!organizationId,
  })

  const showCurrencyToggle = useMemo(() => {
    if (!orgCurrencies) return false
    const codes = orgCurrencies.map((c) => c.currencyCode.toUpperCase())
    return (
      codes.includes(ExtendCurrency.Usd) && codes.includes(ExtendCurrency.Cad)
    )
  }, [orgCurrencies])

  useEffect(() => {
    if (!showCurrencyToggle) setSelectedCurrency(ExtendCurrency.Usd)
  }, [showCurrencyToggle])

  const dateRangeLabel =
    dateRange.preset === 'custom'
      ? formatReportingDateRange(dateRange)
      : translate(reportingPresetLabelKey[dateRange.preset])
  const questionContextLabel = translate(
    'Reporting · ${dateRange} · all card programmes, all departments',
    { dateRange: dateRangeLabel },
  )

  const sharedPanelProps = useMemo(
    () => ({
      organizationId,
      currency: selectedCurrency,
      enabled: !!organizationId,
      dateRange,
      onAskQuestion,
      onNavigate,
      resolveGatedDestination,
      questionContextLabel,
    }),
    [
      dateRange,
      onAskQuestion,
      onNavigate,
      organizationId,
      questionContextLabel,
      resolveGatedDestination,
      selectedCurrency,
    ],
  )

  const toolbar = (
    <div css={style.toolbar} data-testid="reporting-toolbar">
      <ReportingDateRangeControl
        dateRange={dateRange}
        onChange={handleDateRangeChange}
      />
      {!embedded && onCreateReport && (
        <button type="button" onClick={onCreateReport}>
          {translate('Create report')}
        </button>
      )}
      {showCurrencyToggle && (
        <LabelToggle
          name="reporting-currency"
          role="radiogroup"
          aria-label={translate('Display currency')}
          options={currencyOptions}
          value={selectedCurrency}
          onChange={handleCurrencyChange}
        />
      )}
    </div>
  )
  const charts = (
    <div css={style.chartsGrid} data-testid="reporting-breakdown-grid">
      <TotalCompanySpendPanel {...sharedPanelProps} />
      <SpendByPersonPanel {...sharedPanelProps} />
      <SpendByAccountingCategoryPanel {...sharedPanelProps} />
      <SpendByDepartmentPanel {...sharedPanelProps} />
      <SpendByVendorPanel {...sharedPanelProps} />
    </div>
  )

  return (
    <>
      <Helmet>
        <title>{translate('Reporting')}</title>
      </Helmet>
      {embedded ? (
        <div css={style.page}>
          {toolbar}
          {charts}
        </div>
      ) : (
        <LayoutPage
          title={<span>{translate('Reporting')}</span>}
          subtitle={
            <span css={style.subtitle}>
              {translate(
                'Review company spend across accounts, teams, merchants, and people.',
              )}
            </span>
          }
          actions={toolbar}
        >
          <div css={style.page}>{charts}</div>
        </LayoutPage>
      )}
    </>
  )
}

export default Reporting
